import { Field, Input, InputProps, makeStyles } from '@fluentui/react-components';
import { FormikErrors } from 'formik';
import { Column, Row } from '../common';
import { Address } from '../../data/types';
import { useValidation } from '../../data/types/validation';

const useStyles = makeStyles({
    stateZip: {
        minWidth: '50px',
    },
});

export type AddressControlProps = {
    address?: Address;
    errors?: FormikErrors<Address>;
    onChange?: InputProps['onChange'];
    /** Prefix for the field names, so `address` produces `address.city`. */
    namePrefix?: string;
    testIdPrefix?: string;
    /** First tab index; the remaining fields follow in address1, address2, city, state, zip order. */
    startTabIndex?: number;
};
export const AddressControl = (props: AddressControlProps) => {
    const { address, errors, onChange, namePrefix = 'address', testIdPrefix = 'address', startTabIndex } = props;

    //Styles
    const styles = useStyles();

    //Hooks
    const { validateAddress } = useValidation();

    //Logic
    const fieldName = (field: keyof Address) => (!!namePrefix ? `${namePrefix}.${field}` : field);
    const tabIndex = (offset: number) => (startTabIndex === undefined ? undefined : startTabIndex + offset);

    return (
        <Row>
            <Column>
                <Field label='Address' size='medium' validationMessage={errors?.address1}>
                    <Input
                        name={fieldName('address1')}
                        value={address?.address1 || ''}
                        onChange={onChange}
                        tabIndex={tabIndex(0)}
                        placeholder='Enter Address'
                        maxLength={validateAddress.address1.maxLength}
                        data-testid={`${testIdPrefix}-address1-input`}
                    />
                </Field>
                <Field label='Address 2' size='medium' validationMessage={errors?.address2}>
                    <Input
                        name={fieldName('address2')}
                        value={address?.address2 || ''}
                        onChange={onChange}
                        tabIndex={tabIndex(1)}
                        placeholder='Enter Address 2'
                        maxLength={validateAddress.address2.maxLength}
                        data-testid={`${testIdPrefix}-address2-input`}
                    />
                </Field>
            </Column>
            <Column>
                <Field label='City' size='medium' validationMessage={errors?.city}>
                    <Input
                        name={fieldName('city')}
                        value={address?.city || ''}
                        onChange={onChange}
                        tabIndex={tabIndex(2)}
                        placeholder='Enter City'
                        maxLength={validateAddress.city.maxLength}
                        data-testid={`${testIdPrefix}-city-input`}
                    />
                </Field>
                <Row gap='large'>
                    <Column>
                        <Field className={styles.stateZip} label='State' size='medium' validationMessage={errors?.state}>
                            <Input
                                className={styles.stateZip}
                                name={fieldName('state')}
                                value={address?.state || ''}
                                onChange={onChange}
                                tabIndex={tabIndex(3)}
                                placeholder='Enter State'
                                maxLength={validateAddress.state.maxLength}
                                data-testid={`${testIdPrefix}-state-input`}
                            />
                        </Field>
                    </Column>
                    <Column>
                        <Field className={styles.stateZip} label='Zip' size='medium' validationMessage={errors?.zip}>
                            <Input
                                className={styles.stateZip}
                                name={fieldName('zip')}
                                value={address?.zip || ''}
                                onChange={onChange}
                                tabIndex={tabIndex(4)}
                                placeholder='Enter Zip'
                                maxLength={validateAddress.zip.maxLength}
                                data-testid={`${testIdPrefix}-zip-input`}
                            />
                        </Field>
                    </Column>
                </Row>
            </Column>
        </Row>
    );
};
